import ApiService from '../api'

class ProfileService {
  // Add the missing get method that your Vue component is calling
  async get(endpoint) {
    try {
      const response = await ApiService.get(endpoint)

      return response
    } catch (error) {
      console.error('ProfileApiService: Error in GET request', { endpoint, error })
      throw error
    }
  }

  // Add the missing post method that your Vue component is calling
  async post(endpoint, headers = {}, data = {}, additionalHeaders = {}) {
    try {
      return await ApiService.post(endpoint, headers, data, additionalHeaders)
    } catch (error) {
      console.error(`POST ${endpoint} error:`, error)
      throw error
    }
  }

  async getProfile() {
    try {
      const response = await ApiService.get('/my-profile')
      if (response?.Success && response?.Data) {
        return response.Data
      } else if (response?.success && response?.data) {
        return response.data
      } else if (response?.Data) {
        return response.Data
      } else if (response?.data) {
        return response.data
      } else if (response?.user) {
        return {
          user: response.user,
          personal: response.personal || {},
          fellowship: response.fellowship || {},
          grants: response.grants || [],
          publications: response.publications || [],
          projects: response.projects || []
        }
      } else {
        console.warn('ProfileApiService: Unexpected response structure', response)
        return {
          user: response || {},
          personal: {},
          fellowship: {},
          grants: [],
          publications: [],
          projects: []
        }
      }
    } catch (error) {
      console.error('ProfileApiService: Error fetching profile', error)

      if (error?.response?.status === 401) {
        const errorMsg = error.response?.data?.message || 'Authentication failed'
        if (errorMsg.includes('impersonation') || errorMsg.includes('token')) {
          throw new Error('Impersonation session expired. Please refresh the page.')
        }
        throw new Error('Authentication failed. Please log in again.')
      } else if (error?.response?.status === 403) {
        throw new Error('You do not have permission to access this profile.')
      }

      throw error
    }
  }

  // ADDED: Debug method to help troubleshoot profile data loading
  async debugProfileData() {
    try {
      return await ApiService.get('/my-profile')
    } catch (error) {
      console.error('Debug failed:', error)
      return null
    }
  }

  // Update user profile data - Updated for new API structure
  async updateProfile(profileData) {
    try {
      const response = await ApiService.post('/my-profile', {}, profileData)

      if (response.Success || response.success) {
        return response.Data || response.data || response
      } else {
        throw new Error(response.Message || response.message || 'Failed to update profile')
      }
    } catch (error) {
      console.error('ProfileApiService: Error updating profile', error)

      // Enhanced error handling for impersonation scenarios
      if (error?.response?.status === 401) {
        throw new Error('Session expired. Please refresh the page and try again.')
      } else if (error?.response?.status === 403) {
        throw new Error('You do not have permission to update this profile.')
      } else if (error?.response?.status === 422) {
        const validationErrors = error.response?.data?.errors
        if (validationErrors) {
          const errorMessages = Object.values(validationErrors).flat()
          throw new Error('Validation failed: ' + errorMessages.join(', '))
        }
        throw new Error('Validation failed. Please check your input.')
      }

      throw error
    }
  }

  // Get specific profile section
  async getProfileSection(section) {
    try {
      const validSections = ['personal', 'fellowship', 'grants', 'publications', 'projects']
      if (!validSections.includes(section)) {
        throw new Error(`Invalid section. Must be one of: ${validSections.join(', ')}`)
      }

      const response = await ApiService.get(`/my-profile/${section}`)

      if (response && response.success) {
        return response.data
      }
      return response
    } catch (error) {
      console.error(`Get ${section} error:`, error)
      throw error
    }
  }

  // Update specific profile section
  async updateProfileSection(section, sectionData) {
    try {
      const updateData = { [section]: sectionData }
      return await this.updateProfile(updateData)
    } catch (error) {
      console.error(`Update ${section} error:`, error)
      throw error
    }
  }

  // Update personal details
  async updatePersonalDetails(personalData) {
    try {
      return await ApiService.post('/my-profile', {}, {
        personal: personalData
      })
    } catch (error) {
      console.error('Update personal details error:', error)
      throw error
    }
  }

  // Update fellowship details
  async updateFellowship(fellowshipData) {
    try {
      return await ApiService.post('/my-profile', {}, {
        fellowship: fellowshipData
      })
    } catch (error) {
      console.error('Update fellowship error:', error)
      throw error
    }
  }

  // Update grants
  async updateGrants(grantsData) {
    try {
      return await ApiService.post('/my-profile', {}, {
        grants: grantsData
      })
    } catch (error) {
      console.error('Update grants error:', error)
      throw error
    }
  }

  // Add single grant
  async addGrant(grantData) {
    try {
      // Get current profile to append new grant
      const profile = await this.getProfile()
      const existingGrants = profile.grants || []

      const updatedGrants = [...existingGrants, grantData]
      return await this.updateGrants(updatedGrants)
    } catch (error) {
      console.error('Add grant error:', error)
      throw error
    }
  }

  async importPublication(publicationData) {
    try {
      return await ApiService.post(
        '/my-profile/import-publication',
        {},
        publicationData
      )
    } catch (error) {
      console.error('Import publication error:', error)
      throw error
    }
  }


  // Update publications
  async updatePublications(publicationsData) {
    try {
      return await ApiService.post('/my-profile', {}, {
        publications: publicationsData
      })
    } catch (error) {
      console.error('Update publications error:', error)
      throw error
    }
  }

  // Add single publication
  async addPublication(publicationData) {
    try {
      const profile = await this.getProfile()
      const existingPublications = profile.publications || []

      const updatedPublications = [...existingPublications, publicationData]
      return await this.updatePublications(updatedPublications)
    } catch (error) {
      console.error('Add publication error:', error)
      throw error
    }
  }

  // Update projects
  async updateProjects(projectsData) {
    try {
      return await ApiService.post('/my-profile', {}, {
        projects: projectsData
      })
    } catch (error) {
      console.error('Update projects error:', error)
      throw error
    }
  }

  // Add single project
  async addProject(projectData) {
    try {
      const profile = await this.getProfile()
      const existingProjects = profile.projects || []

      const updatedProjects = [...existingProjects, projectData]
      return await this.updateProjects(updatedProjects)
    } catch (error) {
      console.error('Add project error:', error)
      throw error
    }
  }

  // Get profile overview statistics - Updated
  async getOverview() {
    try {
      const profile = await this.getProfile()

      // Calculate statistics from profile data
      const overview = {
        fellowshipCount: profile.fellowship ? 1 : 0,
        grantsCount: Array.isArray(profile.grants) ? profile.grants.length : 0,
        publicationsCount: Array.isArray(profile.publications) ? profile.publications.length : 0,
        projectsCount: Array.isArray(profile.projects) ? profile.projects.length : 0,
        studentsCount: Array.isArray(profile.personal?.studentsSupervised)
          ? profile.personal.studentsSupervised.length : 0,
        totalFunding: this.calculateTotalFunding(profile.grants || []),
        completionPercentage: this.calculateProfileCompleteness(profile)
      }

      return overview
    } catch (error) {
      console.error('Get overview error:', error)
      return {
        fellowshipCount: 0,
        grantsCount: 0,
        publicationsCount: 0,
        projectsCount: 0,
        studentsCount: 0,
        totalFunding: 0,
        completionPercentage: 0
      }
    }
  }

  // Calculate total funding from grants
  calculateTotalFunding(grants) {
    return grants.reduce((total, grant) => {
      const amount = parseFloat(grant.amount) || 0
      return total + amount
    }, 0)
  }

  // Calculate profile completeness percentage
  calculateProfileCompleteness(profile) {
    let totalFields = 0
    let completedFields = 0

    // Check personal section
    if (profile.personal) {
      const personalFields = ['title', 'currentOrganisation', 'currentJobTitle']
      personalFields.forEach(field => {
        totalFields++
        if (profile.personal[field] && profile.personal[field].trim()) {
          completedFields++
        }
      })
    }

    // Check fellowship section
    if (profile.fellowship) {
      const fellowshipFields = ['fellow', 'programme', 'title', 'startDate']
      fellowshipFields.forEach(field => {
        totalFields++
        if (profile.fellowship[field] && profile.fellowship[field].toString().trim()) {
          completedFields++
        }
      })
    }

    // Check arrays
    totalFields += 4 // grants, publications, projects, education
    if (Array.isArray(profile.grants) && profile.grants.length > 0) completedFields++
    if (Array.isArray(profile.publications) && profile.publications.length > 0) completedFields++
    if (Array.isArray(profile.projects) && profile.projects.length > 0) completedFields++
    if (Array.isArray(profile.personal?.education) && profile.personal.education.length > 0) completedFields++

    return totalFields > 0 ? Math.round((completedFields / totalFields) * 100) : 0
  }

  // FIXED: Search for related fellows with better response handling
  async searchRelatedFellows(params = {}) {
    try {
      const searchParams = {
        ...params,
        limit: params.limit || 10,
      }

      // Try the fellowship data endpoint first for better user data
      let response = await ApiService.get('/users/fellowship-data', searchParams)

      // Fallback to regular users endpoint if fellowship data fails
      if (!response || (!response.Success && !response.success)) {
        response = await ApiService.get('/users', searchParams)
      }

      let usersData = []

      // Handle different response structures
      if (response?.Success && response?.Data) {
        usersData = Array.isArray(response.Data.data) ? response.Data.data :
          Array.isArray(response.Data) ? response.Data : []
      } else if (response?.success && response?.data) {
        usersData = Array.isArray(response.data.data) ? response.data.data :
          Array.isArray(response.data) ? response.data : []
      } else if (response?.Data && Array.isArray(response.Data)) {
        usersData = response.Data
      } else if (response?.data && Array.isArray(response.data)) {
        usersData = response.data
      } else if (Array.isArray(response)) {
        usersData = response
      } else {
        console.warn('Related fellows response is not an array:', response)
        return []
      }

      // Transform users to fellow format with better avatar handling
      return usersData.map(user => ({
        id: user.id,
        name: user.name || 'Unknown',
        title: user.title || 'Fellow',
        avatar: user.avatar || user.avatar_url || `https://avatar.iran.liara.run/public/boy?username=${encodeURIComponent(user.name || 'User')}`,
        organization: user.current_organisation || user.fellow_details?.current_organisation || 'N/A'
      }))
    } catch (error) {
      console.error('Search related fellows error:', error)
      return []
    }
  }

  // ENHANCED: Impersonation status checking
  async checkImpersonationStatus() {
    try {
      const UserService = await import('@/services/users/UserService')
      const response = await UserService.default.getImpersonationStatus()

      return {
        isImpersonating: response?.Data?.data?.is_impersonating || response?.data?.is_impersonating || false,
        impersonatedUser: response?.Data?.data?.impersonated_user || response?.data?.impersonated_user || null,
        originalUser: response?.Data?.data?.original_user || response?.data?.original_user || null
      }
    } catch (error) {
      console.warn('Could not check impersonation status:', error)
      return {
        isImpersonating: false,
        impersonatedUser: null,
        originalUser: null
      }
    }
  }

  // ENHANCED: Get activity timeline with better error handling
  async getActivityTimeline(params = {}) {
    try {
      const response = await ApiService.get('/my-profile/activity-timeline', params)

      // Handle different response structures
      if (response?.Data?.success && Array.isArray(response?.Data?.data)) {
        return response.Data.data
      } else if (response?.success && Array.isArray(response?.data)) {
        return response.data
      } else if (response?.Data && Array.isArray(response?.Data)) {
        return response.Data
      } else if (response?.data && Array.isArray(response?.data)) {
        return response.data
      } else if (Array.isArray(response)) {
        return response
      }

      console.warn('No activities found in response structure:', Object.keys(response || {}))
      return []
    } catch (error) {
      console.error('Error fetching activity timeline:', error)

      // Don't throw error for activity timeline to avoid breaking profile
      // But log more details if it's an auth error during impersonation
      if (error?.response?.status === 401) {
        console.warn('Authentication failed for activity timeline - may be impersonation issue')
      }

      return []
    }
  }

  // ENHANCED: Activity stats with better error handling
  async getActivityStats(params = {}) {
    try {
      const response = await ApiService.get('/my-profile/activity-stats', params)

      if (response?.Success && response?.Data) {
        return response.Data
      } else if (response?.success && response?.data) {
        return response.data
      }

      return {
        total_activities: 0,
        profile_updates: 0,
        achievements: 0,
        milestones: 0,
        recent_activity_date: null,
        activity_breakdown: {}
      }
    } catch (error) {
      console.error('Error fetching activity stats:', error)
      return {
        total_activities: 0,
        profile_updates: 0,
        achievements: 0,
        milestones: 0,
        recent_activity_date: null,
        activity_breakdown: {}
      }
    }
  }

  // Upload profile image
  async uploadProfileImage(file) {
    try {
      if (!file || !file.type.startsWith('image/')) {
        throw new Error('Please select a valid image file')
      }

      if (file.size > 5 * 1024 * 1024) {
        throw new Error('Image file size must be less than 5MB')
      }

      const formData = new FormData()
      formData.append('profile_image', file)
      formData.append('upload_type', 'profile_avatar')

      const response = await ApiService.post('/my-profile/upload-image', {}, formData, {
        'Content-Type': 'multipart/form-data'
      })
      return response
    } catch (error) {
      console.error('Upload image error:', error)
      throw error
    }
  }

  // ENHANCED: Get user by ID with better response handling
  async getUser(userId) {
    try {
      const response = await ApiService.get(`/users/${userId}`)

      if (response?.Success && response?.Data) {
        return response.Data
      } else if (response?.success && response?.data) {
        return response.data
      } else if (response?.Data) {
        return response.Data
      } else if (response?.data) {
        return response.data
      }

      return response
    } catch (error) {
      console.error('Get user error:', error)
      throw error
    }
  }

  // Get all users/fellows
  async getUsers(params = {}) {
    try {
      const response = await ApiService.get('/users', params)
      if (response && Array.isArray(response)) {
        return response
      } else if (response && response.success && Array.isArray(response.data)) {
        return response.data
      } else if (response && response.Data && Array.isArray(response.Data)) {
        return response.Data
      } else if (response && response.data && Array.isArray(response.data)) {
        return response.data
      } else {
        console.warn('Users response is not in expected format:', response)
        return []
      }
    } catch (error) {
      console.error('Users not found:', error)
      return []
    }
  }

  // Enhanced validation for new form structure
  validateProfileData(profileData) {
    const errors = []

    // Fellowship validation
    if (profileData.fellowship) {
      const fellowship = profileData.fellowship

      if (!fellowship.fellow?.trim()) {
        errors.push('Fellow name is required')
      }

      if (fellowship.startDate && fellowship.endDate) {
        const startDate = new Date(fellowship.startDate)
        const endDate = new Date(fellowship.endDate)
        if (startDate >= endDate) {
          errors.push('Fellowship end date must be after start date')
        }
      }

      if (fellowship.phdStudy?.subjects &&
        (isNaN(fellowship.phdStudy.subjects) || fellowship.phdStudy.subjects < 0)) {
        errors.push('Number of subjects must be a valid positive number')
      }
    }

    // Personal validation
    if (profileData.personal) {
      const personal = profileData.personal

      if (personal.email && !this.isValidEmail(personal.email)) {
        errors.push('Please enter a valid email address')
      }

      if (personal.linkedinLink && !this.isValidUrl(personal.linkedinLink)) {
        errors.push('Please enter a valid LinkedIn URL')
      }

      if (personal.facebookLink && !this.isValidUrl(personal.facebookLink)) {
        errors.push('Please enter a valid Facebook URL')
      }

      // Validate education entries
      if (personal.education && Array.isArray(personal.education)) {
        personal.education.forEach((edu, index) => {
          if (edu.year && !this.isValidDate(edu.year)) {
            errors.push(`Education ${index + 1}: Invalid year`)
          }
        })
      }
    }

    // Grant validation
    if (profileData.grants && Array.isArray(profileData.grants)) {
      profileData.grants.forEach((grant, index) => {
        if (grant.amount && (isNaN(grant.amount) || grant.amount < 0)) {
          errors.push(`Grant ${index + 1}: Amount must be a valid positive number`)
        }
        if (grant.startYear && grant.endYear && grant.startYear >= grant.endYear) {
          errors.push(`Grant ${index + 1}: End year must be after start year`)
        }
      })
    }

    // Publication validation
    if (profileData.publications && Array.isArray(profileData.publications)) {
      profileData.publications.forEach((pub, index) => {
        if (!pub.title?.trim()) {
          errors.push(`Publication ${index + 1}: Title is required`)
        }
        if (pub.date && !this.isValidDate(pub.date)) {
          errors.push(`Publication ${index + 1}: Invalid publication date`)
        }

        // Validate external identifiers
        if (pub.externalIdentifiers && Array.isArray(pub.externalIdentifiers)) {
          pub.externalIdentifiers.forEach((identifier, idIndex) => {
            if (identifier.fullUrl && !this.isValidUrl(identifier.fullUrl)) {
              errors.push(`Publication ${index + 1}, Identifier ${idIndex + 1}: Invalid URL`)
            }
          })
        }
      })
    }

    // Projects validation
    if (profileData.projects && Array.isArray(profileData.projects)) {
      profileData.projects.forEach((project, index) => {
        if (!project.title?.trim()) {
          errors.push(`Project ${index + 1}: Title is required`)
        }

        if (project.startDate && project.endDate) {
          const startDate = new Date(project.startDate)
          const endDate = new Date(project.endDate)
          if (startDate >= endDate) {
            errors.push(`Project ${index + 1}: End date must be after start date`)
          }
        }
      })
    }

    return {
      isValid: errors.length === 0,
      errors
    }
  }

  // Utility validation methods
  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email?.trim())
  }

  isValidUrl(url) {
    try {
      new URL(url)
      return true
    } catch {
      return false
    }
  }

  isValidDate(dateString) {
    const date = new Date(dateString)
    return date instanceof Date && !isNaN(date)
  }

  // Utility formatting methods
  formatDate(dateString, options = {}) {
    if (!dateString) return 'N/A'
    try {
      const date = new Date(dateString)
      const defaultOptions = {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        ...options
      }
      return date.toLocaleDateString('en-US', defaultOptions)
    } catch {
      return 'Invalid Date'
    }
  }

  formatCurrency(amount, currency = 'USD') {
    if (!amount || isNaN(amount)) return 'N/A'
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency
      }).format(amount)
    } catch {
      return `${currency} ${amount}`
    }
  }

  debounce(func, wait = 300) {
    let timeout
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout)
        func(...args)
      }
      clearTimeout(timeout)
      timeout = setTimeout(later, wait)
    }
  }

  // Helper method to create empty form structures
  getEmptyFormStructure() {
    return {
      personal: {
        title: '',
        currentOrganisation: '',
        currentJobTitle: '',
        country: '',
        biography: '',
        linkedinLink: '',
        twitterLink: '',
        facebookLink: '',
        email: '',
        specialisations: [],
        workingGroups: [],
        education: [],
        professionalPositions: [],
        boardMemberships: [],
        otherExperiences: [],
        studentsSupervised: [],
        awards: []
      },
      fellowship: {
        fellow: '',
        gender: '',
        programme: '',
        call: '',
        title: '',
        callDate: '',
        startDate: '',
        endDate: '',
        projectStart: '',
        objectives: '',
        studyDesign: '',
        projectSummary: '',
        hostOrg: { department: '', institution: '', country: '' },
        participants: { department: '', institution: '', country: '' },
        students: { type: '', name: '', title: '', country: '', university: '', startDate: '', endDate: '' },
        sites: { name: '' },
        phdStudy: { title: '', university: '', startDate: '', endDate: '', subjects: '', status: '', results: '' }
      },
      grants: [],
      publications: [],
      projects: []
    }
  }
}

// Export singleton instance
export default new ProfileService()
